import React, { memo, useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../config/ThemeContext";
import ServeSwitcher from "./ServeSwitcher";
import LiberoSubDialog from "./LiberoSubDialog";

// SVG icons
const StandardViewIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <rect x="2" y="3" width="4" height="4" />
    <rect x="9" y="3" width="4" height="4" />
    <rect x="16" y="3" width="4" height="1" />
    <rect x="2" y="15" width="4" height="4" />
    <rect x="9" y="15" width="4" height="4" />
    <rect x="16" y="15" width="4" height="4" />
  </svg>
);

const SideViewIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <rect x="3" y="2" width="4" height="4" />
    <rect x="15" y="2" width="4" height="4" />
    <rect x="3" y="9" width="4" height="4" />
    <rect x="15" y="9" width="4" height="4" />
    <rect x="3" y="16" width="4" height="4" />
    <rect x="15" y="16" width="4" height="4" />
  </svg>
);

const RotateIcon = ({ direction }) => (
  <svg 
    viewBox="0 0 24 24" 
    className="w-5 h-5 fill-current"
    style={direction === 'backward' ? { transform: 'scaleX(-1)' } : {}}
  >
     <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
  </svg>
);

const RotationSlot = ({ player, pos, isServing, team, isPreview = false, onClick }) => {
  const theme = useTheme();
  const teamColor = theme[team];
  const isLibero = player?.position?.toLowerCase() === "libero";
  const isCaptain = player?.isCaptain;
  const isMiddleBlocker = player?.position?.toLowerCase() === "middle blocker";

const middleBlockerStyle = isMiddleBlocker 
  ? "relative after:content-['MB'] after:text-[9px] after:font-bold after:text-black-700 after:absolute after:top-[-6px] after:right-[-6px] after:px-1 after:py-0.5 after:bg-gray-300 after:rounded-md after:shadow-sm" 
  : "";

  const baseStyle = "flex items-center justify-center border-2 rounded-lg h-16 w-16 text-xl font-bold transition-all";
  const regularStyle = `${teamColor.bg} ${teamColor.border} ${teamColor.text}`;
  const liberoStyle = `${theme.state.libero.bg} ${theme.state.libero.text} font-bold`;
  const captainRing = isCaptain ? `ring-2 ${teamColor.bg}` : "";
  const captainUnderline = isCaptain ? `underline decoration-4` : "";
  const previewStyle = isPreview ? "opacity-70 scale-95" : "";
  const pointerStyle = onClick ? "cursor-pointer" : "";
  const boxClasses = [baseStyle, isLibero ? liberoStyle : regularStyle, captainRing, captainUnderline, middleBlockerStyle, previewStyle, pointerStyle].join(" ");

  return (
    <div
      className={boxClasses}
      title={player ? `${player.shirtNumber} - ${player.shirtName || "Unknown"}` : "No player"}
      onClick={onClick}
    >
      {player ? (
        <span className="flex items-center justify-center gap-1" title="Currently Serving">
          {player.shirtNumber}
          {pos === 1 && isServing ? <span className="text-2xl">🏐</span> : ""}
        </span>
      ) : "—"}
    </div>
  );
};

const RotationDisplay = memo(({ 
  teamName, 
  rotation = [], 
  playerMap = {}, 
  isServing, 
  team,
  matchId,
  currentSet,
  socket,
  showRotationControls =true,
  shouldFlip = false,
  liberoIds = [],
}) => {
  const [highlighted, setHighlighted] = useState(false);
  const prevRotationRef = useRef(rotation); // Using ref instead of state for previous rotation
  const [isSideView, setIsSideView] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [previewRotation, setPreviewRotation] = useState([]);
  const [rotationDirection, setRotationDirection] = useState(null);
  const theme = useTheme();
  const applyButtonRef = useRef(null);
  const [liberoSub, setLiberoSub] = useState({ mode: null, liberoId: null });

  const cancelRotation = useCallback(() => {
    setShowPreview(false);
    setPreviewRotation([]);
    setRotationDirection(null);
  }, []);
  
  const confirmRotation = () => {
    if (rotationDirection && previewRotation.length) {
      socket.emit("manualRotation", {
                  matchId,
                  team,
                  newRotation: previewRotation,
                  reason: `Manual ${rotationDirection} rotation`,
    });
    }
    cancelRotation();
  };

  const openLiberoDialog = (mode, liberoId) => {
    setLiberoSub({ mode, liberoId });
  };

  const handleLiberoConfirm = (selectedId) => {
    if (!selectedId) {
      setLiberoSub({ mode: null, liberoId: null });
      return;
    }

    let benchPlayerId, courtPlayerId;
    if (liberoSub.mode === "enter") {
      benchPlayerId = liberoSub.liberoId;
      courtPlayerId = selectedId;
    } else {
      benchPlayerId = selectedId;
      courtPlayerId = liberoSub.liberoId;
    }

    socket.emit("substitution", {
      matchId,
      currentSet,
      team,
      substitutions: [{ courtPlayerId, benchPlayerId }],
    });

    setLiberoSub({ mode: null, liberoId: null });
  };
// Add keyboard event handler
const handleKeyDown = useCallback((e) => {
  if (!showPreview) return;
  
  switch(e.key) {
    case 'Escape':
      cancelRotation();
      break;
    case 'Enter':
      confirmRotation();
      break;
    default:
      break;
  }
}, [showPreview, cancelRotation, confirmRotation]);

 
  useEffect(() => {
    // Add event listener when component mounts
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up event listener when component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);



  useEffect(() => {
    // Compare current rotation with previous rotation
    if (JSON.stringify(rotation) !== JSON.stringify(prevRotationRef.current)) {
      setHighlighted(true);
      const timer = setTimeout(() => setHighlighted(false), 1000);
      prevRotationRef.current = rotation; // Update previous rotation
      return () => clearTimeout(timer);
    }
  }, [rotation]);

  // Get the correct background class based on highlight state
  const bgClass = highlighted 
  ? `${theme[team].bg} ${theme[team].border} bg-opacity-50` 
    : "bg-white";

  const toggleView = () => setIsSideView((prev) => !prev);

  const rotationIdsStr = rotation.map(String);
  const benchLiberoIds = liberoIds.filter((id) => !rotationIdsStr.includes(String(id)));
  const benchPlayerIds = Object.keys(playerMap).filter(
    (id) => !rotationIdsStr.includes(String(id))
  );

const handlePreviewRotation = (direction) => {
  if (rotation.length !== 6) return;
  
  const newRotation = [...rotation];
  if (direction === 'forward') {
    // Volleyball rotation pattern:
    // z4 → z3, z3 → z2, z2 → z1
    // z5 → z6, z6 → z1, z1 → z2
    const [z4, z3, z2, z5, z6, z1] = newRotation;
    newRotation[0] = z5; // New z4 is old z5
    newRotation[1] = z4; // New z3 is old z4
    newRotation[2] = z3; // New z2 is old z3
    newRotation[3] = z6; // New z5 is old z6
    newRotation[4] = z1; // New z6 is old z1
    newRotation[5] = z2; // New z1 is old z2
  } else {
    // Reverse rotation pattern:
    // z4 ← z3, z3 ← z2, z2 ← z1
    // z5 ← z6, z6 ← z1, z1 ← z2
    const [z4, z3, z2, z5, z6, z1] = newRotation;
    newRotation[0] = z3; // New z4 is old z3
    newRotation[1] = z2; // New z3 is old z2
    newRotation[2] = z1; // New z2 is old z1
    newRotation[3] = z4; // New z5 is old z4
    newRotation[4] = z5; // New z6 is old z5
    newRotation[5] = z6; // New z1 is old z6
  }
  
  setPreviewRotation(newRotation);
  setRotationDirection(direction);
  setShowPreview(true);
    // Focus the apply button after a small delay to ensure it's rendered
  setTimeout(() => {
    applyButtonRef.current?.focus();
  }, 50);
};
let baseLayout = team === "teamA"
  ? [[5, 4], [6, 3], [1, 2]]
  : [[2, 1], [3, 6], [4, 5]];

const sideViewLayout = shouldFlip
  ? baseLayout.slice().reverse().map(row => row.slice().reverse())
  : baseLayout;

  const getPlayer = (index, usePreview = false) => {
    const rot = usePreview ? previewRotation : rotation;
    return playerMap[rot[index]] ?? null;
  };

const renderRotationGrid = (usePreview = false) => {
  if (!isSideView) {
    return (
      <div className="flex items-center">
        {/* Left rotation button - hidden in preview */}
		
		{/* Libero slots beside the rotation */}
                <div className="flex space-x-2 mt-2 justify-center">
                  {benchLiberoIds.map((playerId) => {
                        const player = playerMap[playerId];
                        if (!player) return null;

                        return (
                          <RotationSlot
                                key={`libero-${team}-${playerId}`}
                                player={player}
                                pos={null}                // not on court, no 🏐 needed
                                isServing={false}
                                team={team}
                                isPreview={true}          // visually distinguishable if needed
                                onClick={() => openLiberoDialog('enter', playerId)}
                          />
                        );
                  })}
                </div>

        <div className={`${usePreview ? 'invisible w-12' : 'w-12'}`}>
          {!usePreview && showRotationControls && (
            <button
              onClick={() => handlePreviewRotation('backward')}
              className={`${theme.buttons.neutral} p-2 rounded-md transition-transform hover:scale-110`}
              title="Rotate Backward"
              disabled={rotation.length !== 6}
            >
              <RotateIcon direction="backward" />
            </button>
          )}
        </div>

        {/* Rotation grid - always centered */}
        <div className="flex-1 flex justify-center">
          <div className="flex flex-col gap-2">
            <div className="flex justify-center gap-2">
              {[4, 3, 2].map((pos, i) => {
                const playerId = usePreview ? previewRotation[i] : rotation[i];
                const player = getPlayer(i, usePreview);
                const clickHandler =
                  !usePreview && player?.position?.toLowerCase() === 'libero'
                    ? () => openLiberoDialog('leave', playerId)
                    : undefined;
                return (
                  <RotationSlot
                    key={`${pos}-${usePreview ? 'preview' : 'current'}`}
                    player={player}
                    pos={pos}
                    isServing={isServing}
                    team={team}
                    isPreview={usePreview}
                    onClick={clickHandler}
                  />
                );
              })}
            </div>
            <div className="flex justify-center gap-2">
              {[5, 6, 1].map((pos, i) => {
                const idx = i + 3;
                const playerId = usePreview ? previewRotation[idx] : rotation[idx];
                const player = getPlayer(idx, usePreview);
                const clickHandler =
                  !usePreview && player?.position?.toLowerCase() === 'libero'
                    ? () => openLiberoDialog('leave', playerId)
                    : undefined;
                return (
                  <RotationSlot
                    key={`${pos}-${usePreview ? 'preview' : 'current'}`}
                    player={player}
                    pos={pos}
                    isServing={isServing}
                    team={team}
                    isPreview={usePreview}
                    onClick={clickHandler}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Right rotation button - hidden in preview */}
		
	
		
        <div className={`${usePreview ? 'invisible w-12' : 'w-12'}`}>
          {!usePreview && showRotationControls && (
            <button
              onClick={() => handlePreviewRotation('forward')}
              className={`${theme.buttons.neutral} p-2 rounded-md transition-transform hover:scale-110`}
              title="Rotate Forward"
              disabled={rotation.length !== 6}
            >
              <RotateIcon direction="forward" />
            </button>
          )}
        </div>
      </div>
    );
  } else {
    // Similar implementation for side view
    return (
      <div className="flex items-center">
	  
	  		{/* Libero slots beside the rotation */}
		<div className="flex space-x-2 mt-2 justify-center">
                {benchLiberoIds.map((playerId) => {
                        const player = playerMap[playerId];
                        if (!player) return null;

                        return (
                          <RotationSlot
                                key={`libero-${team}-${playerId}`}
                                player={player}
                                pos={null}                // not on court, no 🏐 needed
                                isServing={false}
                                team={team}
                                isPreview={true}          // visually distinguishable if needed
                                onClick={() => openLiberoDialog('enter', playerId)}
                          />
                        );
                  })}
                </div>
	  
	  
        <div className={`${usePreview ? 'invisible w-12' : 'w-12'}`}>
          {!usePreview && showRotationControls && (
            <button
              onClick={() => handlePreviewRotation('backward')}
              className={`${theme.buttons.neutral} p-2 rounded-md transition-transform hover:scale-110`}
              title="Rotate Backward"
              disabled={rotation.length !== 6}
            >
              <RotateIcon direction="backward" />
            </button>
          )}
        </div>

        <div className="flex-1 flex justify-center">
          <div className="flex flex-col gap-1 items-center">
            {sideViewLayout.map((row, r) => (
              <div key={r} className="flex gap-2">
                {row.map((zone) => {
                  const idx = getStandardIndexForZone(zone);
                  const playerId = usePreview ? previewRotation[idx] : rotation[idx];
                  const player = getPlayer(idx, usePreview);
                  const clickHandler =
                    !usePreview && player?.position?.toLowerCase() === 'libero'
                      ? () => openLiberoDialog('leave', playerId)
                      : undefined;
                  return (
                    <RotationSlot
                      key={`${zone}-${usePreview ? 'preview' : 'current'}`}
                      player={player}
                      pos={zone}
                      isServing={isServing}
                      team={team}
                      isPreview={usePreview}
                      onClick={clickHandler}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className={`${usePreview ? 'invisible w-12' : 'w-12'}`}>
          {!usePreview && showRotationControls && (
            <button
              onClick={() => handlePreviewRotation('forward')}
              className={`${theme.buttons.neutral} p-2 rounded-md transition-transform hover:scale-110`}
              title="Rotate Forward"
              disabled={rotation.length !== 6}
            >
              <RotateIcon direction="forward" />
            </button>
          )}
        </div>
      </div>
    );
  }
};

  return (
    <div className={`rounded-lg p-4 border shadow-sm transition-colors duration-300 ${bgClass} relative overflow-hidden`}>
      <div className="flex justify-between items-center mb-3">
        <h3 className={`text-md font-semibold ${theme.primary.text}`} title="Currently Serving">
          {teamName} <ServeSwitcher isServing={isServing} matchId={matchId} socket={socket} />
        </h3>
        <div className="flex gap-2">
          
          <button
            onClick={toggleView}
            className={`${theme.buttons.neutral} p-2 rounded-md transition-transform hover:scale-110`}
            title={isSideView ? "Switch to Standard View" : "Switch to Side View"}
          >
            {isSideView ? <StandardViewIcon /> : <SideViewIcon />}
          </button>
        </div>
      </div>

      <div className="transition-all duration-300 ease-in-out">
        {renderRotationGrid(false)}
        
{/* Preview container with animation */}
        <div className={`transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden 
            ${showPreview ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0'}`}>
          {showPreview && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700">
                  Preview Rotation ({rotationDirection === 'forward' ? '↻ Forward' : '↺ Backward'})
                </h4>
                <div className="flex gap-2">
                  <button
                    onClick={cancelRotation}
                    className={`${theme.buttons.tertiary} px-3 py-1 text-sm rounded`}
                  >
                    Cancel
                  </button>
                  <button
				    ref={applyButtonRef}
                    onClick={confirmRotation}
                    className={`${theme.buttons.secondary} px-3 py-1 text-sm rounded`}
					autoFocus
                  >
                    Apply
                  </button>
                </div>
              </div>
              {renderRotationGrid(true)}
            </div>
          )}
        </div>
      </div>
      <LiberoSubDialog
        isOpen={!!liberoSub.mode}
        onCancel={() => setLiberoSub({ mode: null, liberoId: null })}
        onConfirm={handleLiberoConfirm}
        mode={liberoSub.mode}
        libero={playerMap[liberoSub.liberoId]}
        options={(
          liberoSub.mode === 'enter'
            ? rotation.slice(3)
            : benchPlayerIds
        ).map((id) => ({
          id,
          name: playerMap[id]?.shirtName,
          number: playerMap[id]?.shirtNumber,
        }))}
      />
    </div>
  );
});

function getStandardIndexForZone(zone) {
  const zoneOrder = [4, 3, 2, 5, 6, 1];
  return zoneOrder.indexOf(zone);
}

export default RotationDisplay;